import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Camera, ChevronRight, Search, ShieldCheck, Upload } from 'lucide-react';
import { lookupShadeProductLines } from '../utils/shadeProductLookup';
import { trackOutcomeJourneyFailed, trackShadeRangeLookup, trackShadeRangeSelected } from '../utils/analytics';
import './OutcomeLanding.css';

// Refill listings normally share the parent product's shade range. Keep the
// main range when both appear, but retain a refill when it is the only record.
export const collapseRefillShadeRanges = (products = []) => {
  const groups = new Map();
  for (const product of products) {
    const title = String(product?.title || '');
    const isRefill = /\brefill\b/i.test(title);
    const canonicalTitle = title
      .replace(/\brefill\b/gi, ' ')
      .replace(/\bfoundation\b\s*$/i, '')
      .replace(/[^a-z0-9]+/gi, ' ')
      .trim()
      .toLowerCase();
    const key = `${String(product?.brand || '').trim().toLowerCase()}|${canonicalTitle}`;
    const current = groups.get(key);
    if (!current || (current.isRefill && !isRefill)) groups.set(key, { product, isRefill });
  }
  return [...groups.values()].map(({ product }) => product);
};

const PRIVACY_URL = 'https://sonmi.io/privacy';

// These are tiny UI tiles but the source artwork is much larger. Use the
// 288px WebP versions (still 4x the mobile display size) and warm the secondary
// route icons while the first chooser is on screen, so a tap never has to wait
// for a new image request.
const JOURNEY_ICON_URLS = [
  'skin-analysis',
  'find-shades',
  'product-analysis',
  'chat',
  'whole-face-scan',
  'one-area',
  'specific-product',
  'shade-swatch',
  'look-i-like',
  'compare-products',
].map((name) => `/journey-icons/${name}.webp`);

function HomeArt({ src }) {
  return <span className="outcome-home-art" aria-hidden="true"><img src={src} alt="" decoding="async" /></span>;
}

function Card({ title, detail, art, onClick }) {
  return (
    <button type="button" className="outcome-card" onClick={onClick}>
      {art}
      <span className="outcome-card-copy"><strong>{title}</strong><small>{detail}</small></span>
      <ChevronRight size={20} aria-hidden="true" />
    </button>
  );
}

function PrivacyDisclosure({ open, onToggle }) {
  return (
    <div className={`outcome-privacy ${open ? 'is-open' : ''}`}>
      <button type="button" onClick={onToggle} aria-expanded={open}>
        <ShieldCheck size={16} aria-hidden="true" />
        <span>You control what you share.</span>
        <u>How images are used</u>
      </button>
      {open && (
        <div className="outcome-privacy-copy">
          <p>Before Son:mi analyses your first face photo, we’ll ask for photo-analysis permission. Once you allow it, you won’t be asked again for every photo you choose to share.</p>
          <p>Your photos are kept private. We don’t sell your data, and you can withdraw permission or request deletion.</p>
          <a href={PRIVACY_URL} target="_blank" rel="noreferrer">Read the Privacy Notice</a>
        </div>
      )}
    </div>
  );
}

function SkinPrivacyPromise() {
  return (
    <section className="outcome-skin-privacy" aria-labelledby="outcome-skin-privacy-title">
      <p className="outcome-permission-promise">
        <ShieldCheck size={18} aria-hidden="true" />
        <span>We’ll ask your permission before analysing your first face photo.</span>
      </p>
      <div className="outcome-privacy-card">
        <h2 id="outcome-skin-privacy-title">Your photos stay in your control.</h2>
        <ul>
          <li>Your photos stay private.</li>
          <li>We don’t sell your data.</li>
          <li>You choose what to share, and you can withdraw permission or request deletion.</li>
        </ul>
        <a href={PRIVACY_URL} target="_blank" rel="noreferrer">How we use your photos →</a>
      </div>
    </section>
  );
}

function PhotoActions({ onChoose, cameraLabel = 'Take a photo', libraryLabel = 'Upload a photo', disabled = false }) {
  return (
    <div className="outcome-actions">
      <button type="button" className="outcome-source" disabled={disabled} onClick={() => onChoose('camera')}>
        <span><Camera size={21} /></span><b>{cameraLabel}</b><ChevronRight size={19} />
      </button>
      <button type="button" className="outcome-source" disabled={disabled} onClick={() => onChoose('library')}>
        <span><Upload size={21} /></span><b>{libraryLabel}</b><ChevronRight size={19} />
      </button>
    </div>
  );
}

export default function OutcomeLanding({
  initialScreen = 'home',
  onFullSkin,
  onFocusedSkin,
  onShadeProduct,
  onShadeProductReference,
  onShadeEstimate,
  onShadeLook,
  onProductAnalysis,
  onProductCompare,
  onChatNow,
  onOutcomeSelected,
  findShadeProducts = lookupShadeProductLines,
}) {
  const [screen, setScreen] = useState(initialScreen);
  const rootRef = useRef(null);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [area, setArea] = useState('cheek');
  const [brandQuery, setBrandQuery] = useState('');
  const [brandLookup, setBrandLookup] = useState({ status: 'idle', candidates: [], products: [] });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currentProduct, setCurrentProduct] = useState('');
  const [currentShade, setCurrentShade] = useState('');
  const [targetProduct, setTargetProduct] = useState('');
  const [inspirationNote, setInspirationNote] = useState('');

  useEffect(() => {
    JOURNEY_ICON_URLS.forEach((src) => {
      const image = new Image();
      image.src = src;
    });
  }, []);

  const back = () => {
    if (screen === 'colour-makeup') setScreen('home');
    else if (screen === 'skin' || screen === 'shade') setScreen(initialScreen === 'colour-makeup' ? 'colour-makeup' : 'home');
    else if (screen === 'products') setScreen('home');
    else if (screen === 'full-skin') setScreen('skin');
    else if (screen === 'focused') setScreen('skin');
    else if (screen === 'shade-product') setScreen('shade');
    else if (screen === 'shade-owned' || screen === 'shade-look') setScreen('shade');
  };


  useEffect(() => {
    const scroller = rootRef.current?.closest('.chat-messages-area');
    if (scroller) scroller.scrollTop = 0;
  }, [screen]);

  const heading = (eyebrow, title, intro) => (
    <>
      <div className="outcome-eyebrow">{eyebrow}</div>
      <h1>{title}</h1>
      {intro && <p className="outcome-intro">{intro}</p>}
    </>
  );
  const chooseOutcome = (outcome, next) => {
    onOutcomeSelected?.(outcome);
    setScreen(next);
  };
  const searchBrand = async (value = brandQuery) => {
    const query = value.trim();
    if (query.length < 2) return;
    setSelectedProduct(null);
    setBrandLookup({ status: 'loading', query, candidates: [], products: [] });
    try {
      const result = await findShadeProducts(query);
      const visibleProducts = collapseRefillShadeRanges(result.products || []);
      trackShadeRangeLookup({ status: result.status, productCount: visibleProducts.length, candidateCount: result.candidates?.length || 0 });
      setBrandLookup({ ...result, products: visibleProducts, query });
    } catch {
      trackShadeRangeLookup({ status: 'unavailable' });
      trackOutcomeJourneyFailed('shade_specific_product', 'lookup_unavailable');
      setBrandLookup({ status: 'unavailable', query, candidates: [], products: [] });
    }
  };

  let content;
  if (screen === 'colour-makeup') {
    content = (
      <>
        {heading('Next up', 'Choose what’s next', 'Son:mi can help you understand your skin or find shades and colours that work for you.')}
        <div className="outcome-list outcome-list--compact">
          <Card title="Understand my skin" detail="See what stands out and what to prioritise" art={<HomeArt src="/journey-icons/skin-analysis.webp" />} onClick={() => chooseOutcome('understand_skin', 'skin')} />
          <Card title="Find shades & colours" detail="Explore makeup colours or match a shade" art={<HomeArt src="/journey-icons/find-shades.webp" />} onClick={() => chooseOutcome('find_shades', 'shade')} />
        </div>
      </>
    );
  } else if (screen === 'home') {
    content = (
      <>
        {heading('Personal beauty advice', 'What would you like help deciding?')}
        <div className="outcome-list">
          <Card title="Understand my skin" detail="See what stands out and what to prioritise" art={<HomeArt src="/journey-icons/skin-analysis.webp" />} onClick={() => chooseOutcome('understand_skin', 'skin')} />
          <Card title="Find shades & colours" detail="Match within a product or from a shade you own" art={<HomeArt src="/journey-icons/find-shades.webp" />} onClick={() => chooseOutcome('find_shades', 'shade')} />
          <Card title="Check or compare products" detail="Analyse a formula, with optional personalisation" art={<HomeArt src="/journey-icons/product-analysis.webp" />} onClick={() => chooseOutcome('check_products', 'products')} />
          <Card title="Chat now" detail="Ask a question or share any beauty photo" art={<HomeArt src="/journey-icons/chat.webp" />} onClick={() => { onOutcomeSelected?.('chat_now'); onChatNow?.(); }} />
        </div>
        <PrivacyDisclosure open={privacyOpen} onToggle={() => setPrivacyOpen((value) => !value)} />
      </>
    );
  } else if (screen === 'skin') {
    content = (
      <>
        {heading('Understand my skin', 'What should I analyse?')}
        <div className="outcome-list outcome-list--compact">
          <Card title="Full skin analysis" detail="A complete, guided read of your face" art={<HomeArt src="/journey-icons/whole-face-scan.webp" />} onClick={() => setScreen('full-skin')} />
          <Card title="One area" detail="Focus on your cheek, forehead, chin or jawline" art={<HomeArt src="/journey-icons/one-area.webp" />} onClick={() => setScreen('focused')} />
        </div>
        <SkinPrivacyPromise />
      </>
    );
  } else if (screen === 'full-skin') {
    content = (
      <>
        {heading('Full skin analysis', 'Add a clear photo of your whole face', 'Use the guided camera for the most complete read, or upload a recent front-facing photo.')}
        <div className="outcome-instructions">
          <strong>For the clearest analysis</strong>
          <ul><li>Face soft natural light</li><li>Keep your whole face visible and in focus</li><li>Use clean, bare skin if possible</li></ul>
        </div>
        <PhotoActions onChoose={(mode) => onFullSkin?.({ mode })} cameraLabel="Start guided scan" libraryLabel="Upload a recent photo" />
        <PrivacyDisclosure open={privacyOpen} onToggle={() => setPrivacyOpen((value) => !value)} />
      </>
    );
  } else if (screen === 'focused') {
    content = (
      <>
        {heading('Focused skin analysis', 'Photograph one area', 'The result will describe only the area you choose, not your overall skin type or full face.')}
        <div className="outcome-area-chips" role="group" aria-label="Area to analyse">
          {['cheek', 'forehead', 'chin', 'jawline'].map((item) => (
            <button type="button" key={item} className={area === item ? 'is-selected' : ''} onClick={() => setArea(item)}>{item}</button>
          ))}
        </div>
        <div className="outcome-instructions">
          <strong>For a useful {area} read</strong>
          <ul><li>Fill most of the frame with the area</li><li>Use soft natural light and no filter</li><li>Keep the image sharp and avoid shadows</li></ul>
        </div>
        <PhotoActions onChoose={(mode) => onFocusedSkin?.({ mode, area })} />
        <PrivacyDisclosure open={privacyOpen} onToggle={() => setPrivacyOpen((value) => !value)} />
      </>
    );
  } else if (screen === 'shade') {
    content = (
      <>
        {heading('Find shades & colours', 'What are you matching?')}
        <div className="outcome-list outcome-list--compact">
          <Card title="A specific product" detail="Choose the range, then add your skin" art={<HomeArt src="/journey-icons/specific-product.webp" />} onClick={() => setScreen('shade-product')} />
          <Card title="A shade I already own" detail="Use it to estimate another product" art={<HomeArt src="/journey-icons/shade-swatch.webp" />} onClick={() => setScreen('shade-owned')} />
          <Card title="A look I like" detail="Analyse its colours, then personalise" art={<HomeArt src="/journey-icons/look-i-like.webp" />} onClick={() => setScreen('shade-look')} />
        </div>
      </>
    );
  } else if (screen === 'shade-product') {
    const selectedLabel = selectedProduct
      ? `${selectedProduct.brand} ${selectedProduct.title}`.trim()
      : '';
    const missingRange = ['not_found', 'no_ranges', 'unavailable'].includes(brandLookup.status);
    content = (
      <>
        {heading('Find my shade', 'Choose the exact range', 'Start with the brand. We’ll show its foundation, concealer, tint, lip and blush ranges so the match stays within one product.')}
        <form className="outcome-search-row" onSubmit={(event) => { event.preventDefault(); searchBrand(); }}>
          <label className="outcome-field"><span>Brand</span><input value={brandQuery} onChange={(event) => { setBrandQuery(event.target.value); setBrandLookup({ status: 'idle', candidates: [], products: [] }); setSelectedProduct(null); }} placeholder="e.g. Maybelline" /></label>
          <button type="submit" className="outcome-secondary" disabled={brandQuery.trim().length < 2 || brandLookup.status === 'loading'}><Search size={18} />{brandLookup.status === 'loading' ? 'Searching…' : 'Find ranges'}</button>
        </form>

        {brandLookup.status === 'clarify_brand' && (
          <div className="outcome-lookup-panel" role="status">
            <strong>Did you mean?</strong>
            <div className="outcome-choice-list">
              {brandLookup.candidates.map((candidate) => (
                <button type="button" key={candidate.brand} onClick={() => { setBrandQuery(candidate.brand); searchBrand(candidate.brand); }}>{candidate.brand}<ChevronRight size={17} /></button>
              ))}
            </div>
          </div>
        )}

        {brandLookup.status === 'found' && (
          <div className="outcome-lookup-panel">
            <strong>{brandLookup.brand} ranges</strong>
            <p>Choose one before adding your skin photo.</p>
            <div className="outcome-choice-list">
              {brandLookup.products.map((item) => (
                <button type="button" key={`${item.id || ''}-${item.title}`} className={selectedProduct?.id === item.id && selectedProduct?.title === item.title ? 'is-selected' : ''} aria-pressed={selectedProduct?.id === item.id && selectedProduct?.title === item.title} onClick={() => { setSelectedProduct(item); trackShadeRangeSelected({ brand: item.brand, productType: item.product_type }); }}>
                  <span><b>{item.title}</b>{item.product_type && <small>{item.product_type}</small>}</span><ChevronRight size={17} />
                </button>
              ))}
            </div>
          </div>
        )}

        {missingRange && (
          <div className="outcome-lookup-panel outcome-lookup-panel--missing" role="status">
            <strong>{brandLookup.status === 'unavailable' ? 'Search is unavailable just now' : `We couldn’t find a matching range for “${brandLookup.query}”`}</strong>
            <p>Add a clear label, product page or shade-range screenshot. Son:mi will identify it in chat before asking for your skin.</p>
            <PhotoActions onChoose={(mode) => onShadeProductReference?.({ mode, product: brandLookup.query })} cameraLabel="Photograph the label" libraryLabel="Upload label or screenshot" />
          </div>
        )}

        {selectedProduct && (
          <div className="outcome-selected-range">
            <small>Matching within</small><strong>{selectedLabel}</strong>
            <p className="outcome-match-explainer">Your skin photo lets Son:mi compare your visible depth and undertone with this range, so it can choose a shade rather than guess from the product name.</p>
            <PhotoActions onChoose={(mode) => onShadeProduct?.({ mode, product: selectedLabel, catalogProductId: selectedProduct.id })} cameraLabel="Take a skin photo" libraryLabel="Upload a skin photo" />
          </div>
        )}
        <PrivacyDisclosure open={privacyOpen} onToggle={() => setPrivacyOpen((value) => !value)} />
      </>
    );
  } else if (screen === 'shade-owned') {
    const complete = currentProduct.trim() && currentShade.trim() && targetProduct.trim();
    content = (
      <>
        {heading('Cross-brand estimate', 'Start with the shade you own', 'This can produce an estimate without a skin photo. The result will say exactly what it was based on.')}
        <label className="outcome-field"><span>Current product</span><input value={currentProduct} onChange={(event) => setCurrentProduct(event.target.value)} placeholder="e.g. NARS Light Reflecting" /></label>
        <label className="outcome-field"><span>Current shade</span><input value={currentShade} onChange={(event) => setCurrentShade(event.target.value)} placeholder="e.g. Punjab" /></label>
        <label className="outcome-field"><span>Target product</span><input value={targetProduct} onChange={(event) => setTargetProduct(event.target.value)} placeholder="e.g. Maybelline Fit Me" /></label>
        <button type="button" className="outcome-primary" disabled={!complete} onClick={() => onShadeEstimate?.({ currentProduct, currentShade, targetProduct })}>Get an estimate in chat</button>
      </>
    );
  } else if (screen === 'shade-look') {
    content = (
      <>
        {heading('Colour inspiration', 'Add the look you like', 'We’ll analyse the colours and makeup—not the person’s skin.')}
        <label className="outcome-field outcome-field--note">
          <span>What do you like or want to recreate? <small>optional</small></span>
          <textarea
            value={inspirationNote}
            onChange={(event) => setInspirationNote(event.target.value)}
            placeholder="e.g. the fresh finish, muted lip and soft blush"
            rows={3}
          />
        </label>
        <PhotoActions
          onChoose={(mode) => onShadeLook?.({ mode, note: inspirationNote.trim() })}
          cameraLabel="Photograph the look"
          libraryLabel="Upload an inspiration image"
        />
        <p className="outcome-field-help">The result opens in chat. Add your own skin there if you want a personal match.</p>
        <PrivacyDisclosure open={privacyOpen} onToggle={() => setPrivacyOpen((value) => !value)} />
      </>
    );
  } else {
    content = (
      <>
        {heading('Products', 'What are you deciding?')}
        <div className="outcome-list outcome-list--compact">
          <Card title="Check one product" detail="Use the existing full formula analysis" art={<HomeArt src="/journey-icons/specific-product.webp" />} onClick={onProductAnalysis} />
          <Card title="Compare products" detail="Add 2–4 products and see the trade-offs" art={<HomeArt src="/journey-icons/compare-products.webp" />} onClick={onProductCompare} />
        </div>
      </>
    );
  }

  return (
    <section ref={rootRef} className="outcome-landing" data-screen={screen} aria-label="Choose how Son:mi can help">
      {screen !== 'home' && <button type="button" className="outcome-back" onClick={back}><ArrowLeft size={18} /> Back</button>}
      {content}
    </section>
  );
}
